import { createFileRoute, Navigate, useParams } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Download, Filter, Info, MessageSquare, Plus } from 'lucide-react';
import type { Awareness } from 'y-protocols/awareness';

import { Card, CardContent } from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import { ErrorMessage } from '@/components/ui/error-message';
import { EmptyState } from '@/components/ui/empty-state';
import {
  RAID_REGISTER_TYPE,
  useRegisterListQuery,
  useUpdateRegisterEntryMutation,
  backendOriginToProvenance,
} from '@/lib/api/registers';
import { useCollab } from '@/lib/collab/use-collab';
import { useAuth } from '@/lib/auth/store';
import { useEngagementQuery } from '@/lib/api/engagements';
import { hasIvvModule } from '@/lib/engagement-modules';
import { hashIndex, TeamAvatars } from '@/components/clients/team-avatars';
import {
  DEFAULT_REGISTER_FILTERS,
  DEFAULT_REGISTER_TYPES,
  RegisterFilterBar,
  type RegisterFiltersState,
} from '@/components/registers/filter-bar';
import { RegisterGrid } from '@/components/registers/register-grid';
import { formatDateTime } from '@/lib/utils/format';

/**
 * T-0098 / E10 Wave 4 — Risk / RAID / Findings / Deliverables register screen.
 *
 * Visual contract: `design_handoff_caliber_v2/prototype/screens-engagement.js`
 * → `Screens.register`. Header pattern (h1 + meta + presence avatar stack +
 *   action button cluster) mirrors `riskRow` / `registerFilters` exactly.
 *
 * What this route owns:
 *   - The Yjs collab session for `(engagement_id, register_type)`. When the
 *     relay is connected, vendor_claim + avero_verification swap to
 *     `<YjsCell>`; otherwise the row falls back to REST PATCH.
 *   - The register-type swap. The toolbar moves between the canonical
 *     register_types: `risk_issue` (RAID), `finding`, `deliverable`,
 *     `deviation`, `interview_note`. Each swap re-keys the collab session.
 *   - The client-viewer scope check — when `viewerGrantId` is non-null and
 *     identity is null, every edit affordance is hidden.
 *
 * Backend-not-yet-built tells:
 *   - "Filter": opens the filter card (purely UI scroll-into-view).
 *   - "Export": no CSV export endpoint exists today. Button is disabled
 *     with a "Pending" title — wired up the moment that endpoint lands.
 *
 * Override + re-eval flow: cell + row action buttons carry
 * `data-open-override="<entryId>"`. The global modal host (T-0113) listens
 * for the delegated click and opens the override modal + re-eval tray.
 *
 * Create flow: the header "New RFI" / "Add risk" buttons carry
 * `data-create="rfi"` / `data-create="risk"`. The same global modal host
 * resolves the engagement id from the URL and opens `CreateFormModal`.
 */
export const Route = createFileRoute('/engagements/$engagementId/register')({
  component: RegisterRoute,
});

/**
 * The registers are IV&V surfaces: they hold risks and issues, which only
 * the IV&V modules' workers and screens read or write, and nothing in
 * Discovery or Selection points at them. An engagement with no IV&V module
 * (Discovery and/or Selection only, as the City of Everett on 22 October
 * 2026) has no Registers item in the sidebar, and a bookmark or an old link
 * lands on the engagement overview instead. `replace`, so Back does not
 * bounce here. The decision reads this route's own engagement id, never the
 * location, so it cannot fire for a path outside this route (the Discovery
 * setup redirect lesson). The engagement read is the cached one every screen
 * under the engagement uses.
 */
function RegisterRoute() {
  const { engagementId } = useParams({ from: '/engagements/$engagementId/register' });
  const engagement = useEngagementQuery(engagementId);

  if (engagement.isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-ink-500">
        <Spinner /> Loading engagement…
      </div>
    );
  }
  if (engagement.data && !hasIvvModule(engagement.data.modules_enabled)) {
    return <Navigate to="/engagements/$engagementId" params={{ engagementId }} replace />;
  }
  return <RegisterScreen />;
}

const DEFAULT_REGISTER_TYPE = RAID_REGISTER_TYPE;

function RegisterScreen() {
  const { engagementId } = useParams({ from: '/engagements/$engagementId/register' });
  const [registerType, setRegisterType] = useState(DEFAULT_REGISTER_TYPE);
  const [filters, setFilters] = useState<RegisterFiltersState>(DEFAULT_REGISTER_FILTERS);
  const [density, setDensity] = useState<'comfortable' | 'compact'>('comfortable');

  const identity = useAuth((s) => s.identity);
  const viewerGrantId = useAuth((s) => s.viewerGrantId);
  const isViewer = !!viewerGrantId && !identity;

  // Server-side filters that the API supports today.
  const list = useRegisterListQuery(engagementId, registerType, {
    ...(filters.status !== 'all' ? { status: filters.status } : {}),
  });
  const update = useUpdateRegisterEntryMutation(engagementId, registerType);
  const { client, status } = useCollab(engagementId, registerType);

  // Awareness wiring (presence chip strip + per-cell cursor flags).
  useEffect(() => {
    if (!client || !identity) return;
    const awareness = client.awareness;
    awareness.setLocalStateField('user', {
      id: identity.userId,
      name: identity.displayName || identity.email || 'Editor',
      color: pickColor(identity.userId),
    });
    return () => {
      awareness.setLocalState(null);
    };
  }, [client, identity]);

  const totalCount = list.data?.entries.length ?? 0;

  // Counts by origin (for the filter-bar pills + the "AI proposals pending"
  // banner). Computed from the *full* fetched set so the filter never
  // changes the pill counts (matching prototype `registerFilters`).
  const originCounts = useMemo(() => {
    const acc = { total: 0, ai: 0, edited: 0, manual: 0, proposed: 0 };
    if (!list.data) return acc;
    for (const e of list.data.entries) {
      acc.total += 1;
      const p = backendOriginToProvenance(e.origin, e.status);
      if (p === 'ai_proposed') {
        acc.ai += 1;
        acc.proposed += 1;
      } else if (p === 'ai_confirmed') {
        acc.ai += 1;
      } else if (p === 'consultant_edited') {
        acc.edited += 1;
      } else {
        acc.manual += 1;
      }
    }
    return acc;
  }, [list.data]);

  // Scroll the filter bar into view when the "Filter" header button is hit.
  const filterBarRef = useRef<HTMLDivElement>(null);
  const scrollToFilters = () => {
    filterBarRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  };

  // Live-collab banner. Viewers never see it.
  const liveBanner = (() => {
    if (isViewer) return null;
    if (status === 'degraded') {
      return (
        <BannerNotice tone="amber">
          Live collaboration unavailable — your edits still save via the manual path. Refresh to
          retry.
        </BannerNotice>
      );
    }
    if (status === 'connecting') {
      return (
        <BannerNotice tone="blue" busy>
          Connecting to live collaboration…
        </BannerNotice>
      );
    }
    if (status === 'disconnected') {
      return (
        <BannerNotice tone="amber" busy>
          Reconnecting…
        </BannerNotice>
      );
    }
    return null;
  })();

  const lastUpdated = useMemo(() => {
    if (!list.data || list.data.entries.length === 0) return null;
    const latest = list.data.entries.reduce<string | null>((acc, e) => {
      if (!acc || e.updated_at > acc) return e.updated_at;
      return acc;
    }, null);
    if (!latest) return null;
    return formatDateTime(latest);
  }, [list.data]);

  return (
    <div className="space-y-4">
      {/* Header — h1 + meta + presence avatar stack + action buttons. */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-h1 font-semibold text-ink-900">{titleFor(registerType)}</h1>
          <p className="mt-1 text-meta text-ink-500">
            {humanizeRegisterType(registerType)} · {totalCount} {totalCount === 1 ? 'row' : 'rows'}
            {status === 'connected' && client ? (
              <PresenceViewerCount
                awareness={client.awareness}
                selfClientId={client.doc.clientID}
              />
            ) : null}
            {isViewer ? ' · Viewer (read-only)' : ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {status === 'connected' && client && !isViewer ? (
            <PresenceAvatarStack awareness={client.awareness} selfClientId={client.doc.clientID} />
          ) : null}
          <button
            type="button"
            onClick={scrollToFilters}
            className="inline-flex h-8 items-center gap-1 rounded-chip border border-ink-100 bg-paper-0 px-3 text-meta font-medium text-ink-700 hover:border-ink-300"
          >
            <Filter className="h-3.5 w-3.5" aria-hidden="true" />
            Filter
          </button>
          <button
            type="button"
            disabled
            title="Export — Pending: CSV export endpoint not implemented yet"
            className="inline-flex h-8 cursor-not-allowed items-center gap-1 rounded-chip border border-ink-100 bg-paper-0 px-3 text-meta font-medium text-ink-500 opacity-60"
          >
            <Download className="h-3.5 w-3.5" aria-hidden="true" />
            Export
          </button>
          {isViewer ? null : (
            <>
              <button
                type="button"
                data-create="rfi"
                className="inline-flex h-8 items-center gap-1 rounded-chip border border-ink-300 bg-paper-0 px-3 text-meta font-semibold text-ink-900 hover:bg-ink-100/40"
              >
                <MessageSquare className="h-3.5 w-3.5" aria-hidden="true" />
                New RFI
              </button>
              <button
                type="button"
                data-create={createKindForRegister(registerType)}
                className="inline-flex h-8 items-center gap-1 rounded-chip border border-accent-amber-600 bg-accent-amber-100 px-3 text-meta font-semibold text-accent-amber-700 hover:bg-accent-amber-50"
              >
                <Plus className="h-3.5 w-3.5" aria-hidden="true" />
                {addLabelForRegister(registerType)}
              </button>
            </>
          )}
        </div>
      </div>

      {liveBanner}

      {/* AI-proposed pending review banner (prototype's "N AI-proposed rows
          pending review" chip strip). */}
      {originCounts.proposed > 0 ? (
        <div className="flex flex-wrap items-center gap-2 rounded-card border border-ai-surface-border bg-ai-surface px-3 py-2 text-meta text-ai-ink">
          <Info className="h-3.5 w-3.5" aria-hidden="true" />
          <span>
            <strong>{originCounts.proposed}</strong> AI-proposed{' '}
            {originCounts.proposed === 1 ? 'row' : 'rows'} pending review
          </span>
          <button
            type="button"
            onClick={() =>
              setFilters({ ...filters, origin: 'ai_proposed_or_confirmed', status: 'all' })
            }
            className="ml-2 inline-flex h-6 items-center rounded-chip border border-ai-surface-border bg-paper-0 px-2 text-[11px] font-medium text-ai-ink hover:bg-ai-surface"
          >
            Filter to AI rows
          </button>
        </div>
      ) : null}

      <div ref={filterBarRef}>
        <RegisterFilterBar
          registerType={registerType}
          onRegisterTypeChange={setRegisterType}
          knownRegisterTypes={DEFAULT_REGISTER_TYPES}
          filters={filters}
          onFiltersChange={setFilters}
          density={density}
          onDensityChange={setDensity}
          counts={{
            total: originCounts.total,
            ai: originCounts.ai,
            edited: originCounts.edited,
            manual: originCounts.manual,
          }}
          readOnly={isViewer}
        />
      </div>

      <Card>
        <CardContent className="p-0">
          {list.isLoading ? (
            <div className="flex items-center gap-2 p-4 text-body-sm text-ink-500">
              <Spinner /> Loading entries…
            </div>
          ) : list.error ? (
            <div className="p-4">
              <ErrorMessage error={list.error} />
            </div>
          ) : !list.data || list.data.entries.length === 0 ? (
            <div className="p-4">
              <EmptyState
                title="No entries"
                description={`No ${humanizeRegisterType(registerType).toLowerCase()} entries found for this engagement yet.`}
              />
            </div>
          ) : (
            <RegisterGrid
              entries={list.data.entries}
              filters={filters}
              density={density}
              doc={status === 'connected' ? client?.doc : undefined}
              readOnly={isViewer}
              onManualSave={(entryId, input) =>
                update.mutate({
                  entryId,
                  input,
                  // Bucketed idempotency key — fast double-click safe.
                  idempotencyKey: `web-register-update-${entryId}-${Math.floor(
                    Date.now() / 60_000,
                  )}`,
                })
              }
              savingEntryId={update.isPending ? (update.variables?.entryId ?? null) : null}
              engagementId={engagementId}
            />
          )}
        </CardContent>
      </Card>

      {/* Footer — last update + live-sync state. Mirrors prototype's
          "Last update 14:02 · Yjs sync · N users connected" strip. */}
      <div className="flex flex-wrap items-center justify-between gap-2 text-meta text-ink-500">
        <div className="inline-flex items-center gap-1">
          <Info className="h-3 w-3" aria-hidden="true" />
          AI-proposed rows are tinted. Click any cell to edit. The pencil / Review button on a row
          opens override + re-eval.
        </div>
        <div>
          {lastUpdated ? `Last update ${lastUpdated} · ` : ''}
          {status === 'connected' ? (
            <>
              Yjs sync ·{' '}
              <PresenceConnectedCount
                awareness={client?.awareness}
                selfClientId={client?.doc.clientID ?? 0}
              />
            </>
          ) : status === 'degraded' ? (
            'Yjs sync · unavailable'
          ) : (
            'Yjs sync · connecting…'
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Compact presence summary that renders the avatar stack (up to 4 + N more)
 * for users currently viewing this register — driven by Yjs awareness so it
 * matches the live state. Self is excluded so the chip reads "N collaborators
 * viewing".
 */
function PresenceAvatarStack({
  awareness,
  selfClientId,
}: {
  awareness: Awareness;
  selfClientId: number;
}) {
  const [members, setMembers] = useState<Array<{ id: string; name: string }>>([]);

  useEffect(() => {
    const next = (): void => {
      const seen = new Map<string, { id: string; name: string }>();
      awareness.getStates().forEach((state, clientId) => {
        if (clientId === selfClientId) return;
        const user = (state as { user?: { id?: string; name?: string } }).user;
        if (!user) return;
        const id = user.id ?? `client:${clientId}`;
        if (!seen.has(id)) {
          seen.set(id, { id, name: user.name ?? 'Anonymous' });
        }
      });
      setMembers(Array.from(seen.values()));
    };
    next();
    awareness.on('change', next);
    return () => {
      awareness.off('change', next);
    };
  }, [awareness, selfClientId]);

  if (members.length === 0) return null;
  return <TeamAvatars members={members} max={4} size={24} aria-label="Live collaborators" />;
}

/** Tiny presence subtitle ("· N collaborators viewing"). */
function PresenceViewerCount({
  awareness,
  selfClientId,
}: {
  awareness: Awareness;
  selfClientId: number;
}) {
  const [count, setCount] = useState<number>(0);
  useEffect(() => {
    const next = (): void => {
      let n = 0;
      awareness.getStates().forEach((_state, clientId) => {
        if (clientId !== selfClientId) n += 1;
      });
      setCount(n);
    };
    next();
    awareness.on('change', next);
    return () => {
      awareness.off('change', next);
    };
  }, [awareness, selfClientId]);
  if (count === 0) return null;
  return (
    <>
      {' · '}
      <span>
        {count} {count === 1 ? 'collaborator' : 'collaborators'} viewing
      </span>
    </>
  );
}

/** Footer "N users connected" — includes self. */
function PresenceConnectedCount({
  awareness,
  selfClientId,
}: {
  awareness?: Awareness;
  selfClientId: number;
}) {
  const [count, setCount] = useState<number>(1);
  useEffect(() => {
    if (!awareness) return;
    const next = (): void => {
      let n = 0;
      awareness.getStates().forEach(() => {
        n += 1;
      });
      setCount(Math.max(1, n));
    };
    next();
    awareness.on('change', next);
    return () => {
      awareness.off('change', next);
    };
  }, [awareness, selfClientId]);
  return (
    <span>
      {count} user{count === 1 ? '' : 's'} connected
    </span>
  );
}

function BannerNotice({
  tone,
  busy,
  children,
}: {
  tone: 'amber' | 'blue';
  busy?: boolean;
  children: React.ReactNode;
}) {
  const toneClass =
    tone === 'amber'
      ? 'border-accent-amber-100 bg-accent-amber-50 text-accent-amber-700'
      : 'border-cite-chip-border bg-cite-chip-bg text-cite-chip-ink';
  return (
    <div className={`flex items-center gap-2 rounded-card border px-3 py-2 text-meta ${toneClass}`}>
      {busy ? <Spinner /> : null}
      <span>{children}</span>
    </div>
  );
}

/** Title for the page header per register type. */
function titleFor(registerType: string): string {
  switch (registerType) {
    case RAID_REGISTER_TYPE:
      return 'Risk register';
    case 'finding':
      return 'Health findings';
    case 'deliverable':
      return 'Expected deliverables';
    case 'deviation':
      return 'Deviation register';
    case 'interview_note':
      return 'Interview notes';
    default:
      return humanizeRegisterType(registerType);
  }
}

/** Map the active register_type to the `data-create` kind the global modal
 *  host knows how to render. Defaults to `risk` for unknown types so the
 *  primary button never disappears entirely. */
function createKindForRegister(registerType: string): string {
  switch (registerType) {
    case RAID_REGISTER_TYPE:
      return 'risk';
    case 'finding':
      return 'finding';
    case 'deliverable':
      return 'deliverable';
    case 'deviation':
      return 'risk'; // No deviation kind on CreateFormModal yet; falls back.
    case 'interview_note':
      return 'interview';
    default:
      return 'risk';
  }
}

function addLabelForRegister(registerType: string): string {
  switch (registerType) {
    case RAID_REGISTER_TYPE:
      return 'Add risk';
    case 'finding':
      return 'Add finding';
    case 'deliverable':
      return 'Add deliverable';
    case 'deviation':
      return 'Add deviation';
    case 'interview_note':
      return 'Add interview';
    default:
      return 'Add entry';
  }
}

function humanizeRegisterType(value: string): string {
  const known = DEFAULT_REGISTER_TYPES.find((t) => t.value === value);
  return known?.label ?? value;
}

/**
 * Deterministic per-user color picker for the presence chip. Returns a
 * CSS `var(--avatar-N-bg)` string from the brand avatar palette.
 */
function pickColor(userId: string): string {
  const tone = hashIndex(userId, 6);
  return `var(--avatar-${tone}-bg)`;
}
